/**
 * Page Invite Service
 *
 * Service voor het genereren van invite links voor pagina's
 * Ondersteunt WhatsApp en Email uitnodigingen
 */

export interface PageInviteOptions {
  pageName?: string; // Naam van de pagina (bijv. "Dashboard", "Agenda")
  pageHash?: string; // Hash van de pagina (bijv. "#dashboard", "#agenda")
  customMessage?: string; // Aangepast bericht voor de uitnodiging
  userName?: string; // Naam van de uitnodiger
}

export interface InviteLink {
  url: string;
  whatsappUrl: string;
  emailSubject: string;
  emailBody: string;
  emailLink: string;
}

/**
 * Genereer de volledige URL voor een pagina
 */
export function generatePageUrl(pageHash?: string): string {
  const baseUrl = window.location.origin;
  const hash = pageHash || window.location.hash || '#dashboard';

  // Zorg dat hash begint met #
  const normalizedHash = hash.startsWith('#') ? hash : `#${hash}`;

  return `${baseUrl}${normalizedHash}`;
}

/**
 * Genereer WhatsApp invite link
 */
export function generateWhatsAppLink(
  url: string,
  message: string
): string {
  const encodedMessage = encodeURIComponent(message);
  const encodedUrl = encodeURIComponent(url);
  const fullMessage = `${message}\n\n${url}`;
  const encodedFullMessage = encodeURIComponent(fullMessage);

  // WhatsApp Web API link
  return `https://wa.me/?text=${encodedFullMessage}`;
}

/**
 * Genereer Email invite link (mailto)
 */
export function generateEmailLink(
  subject: string,
  body: string
): string {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);

  return `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
}

/**
 * Genereer alle invite links voor een pagina
 */
export function generateInviteLinks(
  options: PageInviteOptions = {}
): InviteLink {
  const {
    pageName = 'SuperKonnected',
    pageHash,
    customMessage,
    userName
  } = options;

  // Genereer pagina URL - altijd naar home/dashboard
  const pageUrl = generatePageUrl('#dashboard');

  // Standaard bericht - algemene platform uitnodiging
  const defaultMessage = customMessage ||
    `Hoi! Ik wil je graag uitnodigen voor SuperKonnected - het eerste Clean Mind social media platform voor ondernemers. Ontdek een netwerk zonder ruis, gericht op echte verbinding en groei.`;

  const inviteMessage = userName
    ? `${defaultMessage}\n\n- ${userName}`
    : defaultMessage;

  // Email onderwerp - algemeen
  const emailSubject = `Uitnodiging voor SuperKonnected - Social Media 3.0`;

  // Email body
  const emailBody = `${inviteMessage}\n\nMeld je hier aan: ${pageUrl}`;

  // WhatsApp link
  const whatsappUrl = generateWhatsAppLink(pageUrl, inviteMessage);

  // Email link
  const emailLink = generateEmailLink(emailSubject, emailBody);

  return {
    url: pageUrl,
    whatsappUrl,
    emailSubject,
    emailBody,
    emailLink
  };
}

/**
 * Kopieer link naar clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback voor oudere browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
}

/**
 * Open WhatsApp in nieuw venster/tab
 */
export function openWhatsApp(whatsappUrl: string): void {
  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Open email client
 */
export function openEmail(emailLink: string): void {
  window.location.href = emailLink;
}
